const overflowStates = new WeakMap();

const resolveElements = (root) => {
  const container =
    root.querySelector(':scope > .p-datatable-table-container') ||
    root.querySelector('.p-datatable-table-container');
  const scrollElement = container?.querySelector('.p-virtualscroller') || container;
  const table = container?.querySelector('.p-datatable-table');

  return { container, scrollElement, table };
};

const syncOverflow = (root) => {
  const { scrollElement, table } = resolveElements(root);

  if (!scrollElement) {
    root.removeAttribute('data-horizontal-overflow');
    return;
  }

  const scrollWidth = Math.max(scrollElement.scrollWidth, table?.scrollWidth || 0);
  root.toggleAttribute('data-horizontal-overflow', scrollWidth > scrollElement.clientWidth + 1);
};

const mount = (root) => {
  let frame = null;
  const observedResizeElements = new Set();
  const resizeObserver =
    typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
  const mutationObserver =
    typeof MutationObserver === 'undefined'
      ? null
      : new MutationObserver(() => {
          syncResizeTargets();
          schedule();
        });

  function syncResizeTargets() {
    if (!resizeObserver) {
      return;
    }

    const { container, scrollElement, table } = resolveElements(root);
    const nextElements = new Set([root, container, scrollElement, table].filter(Boolean));

    observedResizeElements.forEach((element) => {
      if (!nextElements.has(element)) {
        resizeObserver.unobserve(element);
        observedResizeElements.delete(element);
      }
    });

    nextElements.forEach((element) => {
      if (!observedResizeElements.has(element)) {
        resizeObserver.observe(element);
        observedResizeElements.add(element);
      }
    });
  }

  function schedule() {
    if (frame !== null) {
      return;
    }

    frame = window.requestAnimationFrame(() => {
      frame = null;
      syncOverflow(root);
    });
  }

  syncResizeTargets();
  mutationObserver?.observe(root, {
    childList: true,
    subtree: true,
  });
  window.addEventListener('resize', schedule);
  overflowStates.set(root, {
    mutationObserver,
    resizeObserver,
    schedule,
    stop() {
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }

      mutationObserver?.disconnect();
      resizeObserver?.disconnect();
      observedResizeElements.clear();
      window.removeEventListener('resize', schedule);
      root.removeAttribute('data-horizontal-overflow');
    },
  });
  schedule();
};

export default {
  mounted: mount,
  updated(root) {
    overflowStates.get(root)?.schedule();
  },
  unmounted(root) {
    overflowStates.get(root)?.stop();
    overflowStates.delete(root);
  },
};
